import './polyfill.js';
import React from 'react';
import { withPrefix } from 'gatsby';
import RootWrapper from './src/RootWrapper';
import PageWrapper from './src/PageWrapper';
import configureServices from './src/services/index';
import analyticsApiClient from './src/services/Analytics/index';
import context from './src/context/index';
import { sendEventToOptimizely } from './src/optimizely';
import { makeStore, startAppListening } from './src/store';
import { initAppListeners } from './src/store/listenerMiddleware';
import {
  shouldRedirect,
  getBokaSubPathname,
  addPersistingQueryParamsToPath,
  getSubPathsForBoka,
  getPagePath,
  BOKA_QUERY_PARAMS,
} from './src/helpers/urlHelper';
import { setNativeAppTheme } from './src/helpers/nativeAppHelper';
import { loadTranslations } from './src/helpers/translationLoader';
import { isPlainView, isInsideIframe } from './src/helpers/plainViewHelper';

/* globals CSS_CONFIG, MAINTENANCE_MODE, SUB_PATH, TARGET */

const store = makeStore(initAppListeners(startAppListening));

export const wrapRootElement = ({
  element,
}: {
  element: React.ReactElement;
}) => <RootWrapper store={store} element={element} />;

export const wrapPageElement = PageWrapper;

export const onClientEntry = async () => {
  setNativeAppTheme();

  // Load runtime translations from CDN (prod/ppe only)
  if (TARGET === 'prod' || TARGET === 'ppe') {
    try {
      const country = CSS_CONFIG.COUNTRY || 'gb';
      const language = CSS_CONFIG.LANGUAGE || 'en';

      const runtimeTranslations = await loadTranslations(
        country,
        language,
        TARGET,
      );

      // Store translations on window object, to use when components mount
      if (Object.keys(runtimeTranslations).length > 0) {
        window.__RUNTIME_TRANSLATIONS__ = {
          content: runtimeTranslations,
          ns: 'translations',
        };
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        'Failed to load runtime translations, using build-time fallback:',
        error,
      );
    }
  }

  if (
    MAINTENANCE_MODE === 'true' &&
    window.location.href.indexOf('service-unavailable') === -1
  ) {
    const serviceUnavailableUrl =
      window.location.origin + withPrefix(getPagePath('service-unavailable'));
    window.location.assign(
      addPersistingQueryParamsToPath(serviceUnavailableUrl, {
        exclude: ['vpc', 'projectid', 'imagination', 'event_action'],
      }),
    );
    return;
  }

  const queryParams = new URLSearchParams(window.location.search);

  if (
    isPlainView() &&
    !isInsideIframe() &&
    queryParams.get(BOKA_QUERY_PARAMS.TEST_MODE) !== 'true' &&
    window.location.href.indexOf('service-unavailable') === -1
  ) {
    const serviceUnavailableUrl =
      window.location.origin + withPrefix(getPagePath('service-unavailable'));
    window.location.assign(
      addPersistingQueryParamsToPath(serviceUnavailableUrl, {
        exclude: ['vpc', 'projectid', 'imagination', 'event_action'],
      }),
    );
    return;
  }

  const services = await configureServices();
  context.registerServices(services);

  const { search } = store.getState();
  const redirect = shouldRedirect(CSS_CONFIG, SUB_PATH, search.postCode);

  if (redirect) {
    const [operation] = getSubPathsForBoka(SUB_PATH);
    const path = getPagePath(operation);
    window.location.href = addPersistingQueryParamsToPath(withPrefix(path));
  }
};

const routeHasChanged = (location: Location, prevLocation: Location) => {
  if (!prevLocation) {
    return false;
  }

  const currentPath = getBokaSubPathname(location.pathname, SUB_PATH);
  const prevPath = getBokaSubPathname(prevLocation.pathname, SUB_PATH);
  return currentPath !== prevPath;
};

export const onRouteUpdate = ({
  location,
  prevLocation,
}: {
  location: Location;
  prevLocation: Location;
}) => {
  const { sendEventToEpisod, sendPageview } = analyticsApiClient();
  const { app, search, services, stores } = store.getState();
  const { operation } = app;
  const { serviceId, storeId, replacementService } = app.selected;
  const { pathname } = location;

  if (pathname.endsWith('/services/')) {
    const eventName = `begin_${operation}_appointment`;
    sendEventToEpisod(eventName, {
      postalCode: search.postCode,
      step: '1',
    });
    sendEventToOptimizely(eventName);
  }

  if (pathname.endsWith('/timeslots/')) {
    const eventName = `${serviceId}_${operation}_appointment`;
    sendEventToEpisod(eventName, {
      postalCode: search.postCode,
      step: '2',
      options: {
        serviceId,
        services: services.availableServices,
      },
    });
    sendEventToOptimizely(eventName);
  }

  if (pathname.endsWith('/contact/') && operation && serviceId) {
    if (replacementService.timeSlot) {
      const replacementServiceId = services.availableServices.find(
        (service) => service.serviceId === serviceId,
      )?.replacementService?.serviceId;
      const replacementServiceEventName = `select_replacement_date_and_time_${replacementServiceId}_${operation}_appointment`;
      sendEventToEpisod(replacementServiceEventName, {
        postalCode: search.postCode,
        step: '3',
        options: {
          serviceId,
          replacementServiceId,
          services: services.availableServices,
          storeId: replacementService.storeId,
          stores: stores.replacementServiceStores,
        },
      });
      sendEventToOptimizely(replacementServiceEventName);
      return;
    } else {
      const timeslotEventName = `select_date_and_time_${serviceId}_${operation}_appointment`;
      sendEventToEpisod(timeslotEventName, {
        postalCode: search.postCode,
        step: '3',
        options: {
          serviceId,
          services: services.availableServices,
          storeId,
          stores: stores.availableStores,
        },
      });
      sendEventToOptimizely(timeslotEventName);
    }
  }
  if (pathname.endsWith('/confirm/')) {
    if (CSS_CONFIG.OPTIMIZELYEVENTS.completeBooking) {
      sendEventToOptimizely('booking_complete_global');
      sendEventToOptimizely(`booking_complete_${CSS_CONFIG.COUNTRY}`);
    }
    if (replacementService.timeSlot) {
      const replacementServiceId = services.availableServices.find(
        (service) => service.serviceId === serviceId,
      )?.replacementService?.serviceId;
      const replacementCompleteEventName = `complete_replacement_${replacementServiceId}_${operation}_appointment`;
      sendEventToEpisod(replacementCompleteEventName, {
        postalCode: search.postCode,
        step: '4',
        options: {
          serviceId,
          replacementServiceId,
          services: services.availableServices,
          storeId: replacementService.storeId,
          stores: stores.replacementServiceStores,
        },
      });
      sendEventToOptimizely(replacementCompleteEventName);
      return;
    } else {
      const completeEventName = `complete_${serviceId}_${operation}_appointment`;
      sendEventToEpisod(completeEventName, {
        postalCode: search.postCode,
        step: '4',
        options: {
          serviceId,
          services: services.availableServices,
          storeId,
          stores: stores.availableStores,
        },
      });
      sendEventToOptimizely(completeEventName);
    }
  }

  if (routeHasChanged(location, prevLocation)) {
    sendPageview();
  }
};
